import { TaskRelatedRecords } from '../tasks/TaskRelatedRecords';
import React, { useState, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import { CheckCircle as CheckCircleIcon, Flag as FlagIcon, User as PersonIcon, Calendar as CalendarTodayIcon } from '@phosphor-icons/react';
import type { InboxItem } from '../../types/inbox';
import type { Task } from '../../types/tasks';
import api from '../../lib/api';

interface InlineTaskViewProps {
  item: InboxItem;
  onComplete?: () => void;
}

const InlineTaskView: React.FC<InlineTaskViewProps> = ({ item, onComplete }) => {
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchTask = async () => {
      try {
        setLoading(true);
        setTask(null);
        setError(null);
        
        // Extract numeric ID from source_id (e.g., "task-20" -> "20")
        const taskId = item.source_id.replace('task-', '');
        const response = await api.get<Task>(`/api/tasks/${taskId}/`);
        if (!cancelled) setTask(response.data);
      } catch (err: any) {
        console.error('Failed to fetch task details:', err);
        if (!cancelled) setError(err.message || 'Failed to load task details');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (item.source_id) {
      fetchTask();
    } else {
      setLoading(false);
      setError('No task ID available');
    }
    return () => { cancelled = true; };
  }, [item.source_id]);

  // Task data from API or fallback to inbox item data
  const taskData = {
    description: task?.description || item.preview || 'No description provided',
    dueDate: task?.due_date 
      ? new Date(task.due_date).toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: 'numeric', 
          minute: '2-digit' 
        })
      : 'No due date set',
    assignee: task?.assigned_to_name || item.assigned_to_name || 'Unassigned',
    priority: task?.priority || item.priority || 'normal',
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'danger';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size="sm" />
        <Typography level="body-sm" sx={{ ml: 1 }}>Loading task details...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography level="body-sm" color="danger">
          Error loading task: {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={3}>
        {/* Task Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              {item.subject}
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {taskData.description}
            </Typography>
          </Box>
          {onComplete && <Button
            variant="solid"
            color="success"
            size="sm"
            startDecorator={<CheckCircleIcon />}
            onClick={onComplete}
          >
            Mark Complete
          </Button>}
        </Stack>

        {/* Task Metadata */}
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Chip
            size="sm"
            variant="soft"
            color={getPriorityColor(taskData.priority)}
            startDecorator={<FlagIcon />}
          >
            {taskData.priority.toUpperCase()}
          </Chip>
          <Chip
            size="sm"
            variant="soft"
            startDecorator={<PersonIcon />}
          >
            {taskData.assignee}
          </Chip>
          <Chip
            size="sm"
            variant="soft"
            startDecorator={<CalendarTodayIcon />}
          >
            {taskData.dueDate}
          </Chip>
        </Stack>

        {task && <TaskRelatedRecords task={task} />}
      </Stack>
    </Box>
  );
};

export default InlineTaskView;